import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  LogOut, Plus, Pencil, Trash2, X, Upload, Package, Tags, ExternalLink, Star,
  Sparkles, ShoppingBag, Settings, Film, Link2, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, resolveImg } from "@/lib/api";
import { CATEGORY_ICON_NAMES, iconFor } from "@/lib/categoryIcons";
import { isSupportedVideoLink, embedUrl } from "@/lib/media";
import { ImageEditor } from "@/components/ImageEditor";
import { OrdersPanel } from "@/pages/admin/OrdersPanel";
import { SettingsPanel } from "@/pages/admin/SettingsPanel";

const EMPTY = {
  name: "", description: "", category: "", price: "",
  dimensions: "", weight: "",
  images: [], videos: [], variants: [],
  is_new: false, is_featured: false,
  is_customizable: false, customization_label: "",
  sort_order: 0,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("produtos");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (user === false) nav("/admin/login");
  }, [user, nav]);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeProduct = async (id) => {
    if (!window.confirm("Remover este produto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Produto removido");
    load();
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post("/categories", { name: newCat.trim() });
      setNewCat(""); toast.success("Categoria criada"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // The API refuses to delete a category that still holds products, because
  // they would keep a category name that no longer exists and disappear from
  // every filter. A 409 means we have to ask where they should go.
  const removeCategory = async (category) => {
    try {
      await api.delete(`/categories/${category.id}`);
      toast.success("Categoria removida");
      load();
    } catch (e) {
      if (e.response?.status === 409) setDeleting({ category, detail: e.response.data.detail });
      else toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const confirmRemoveCategory = async (target) => {
    const { category } = deleting;
    const query = target ? `?reassign_to=${encodeURIComponent(target)}` : "?force=true";
    try {
      const { data } = await api.delete(`/categories/${category.id}${query}`);
      toast.success(target
        ? `Categoria removida, ${data.products_moved} produto(s) movido(s) para "${target}"`
        : `Categoria removida, ${data.products_moved} produto(s) ficaram sem categoria`);
      setDeleting(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  /** Reorder the catalogue by renumbering: highest sort_order shows first. */
  const moveProduct = async (id, direction) => {
    const index = products.findIndex((p) => p.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= products.length) return;

    const reordered = [...products];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setProducts(reordered);

    const total = reordered.length;
    const changed = reordered
      .map((p, i) => ({ p, order: total - i }))
      .filter(({ p, order }) => p.sort_order !== order);
    try {
      await Promise.all(changed.map(({ p, order }) =>
        api.put(`/products/${p.id}`, {
          name: p.name, description: p.description, category: p.category,
          price: p.price || null, images: p.images, videos: p.videos,
          is_new: p.is_new, is_featured: p.is_featured,
          is_customizable: p.is_customizable, customization_label: p.customization_label,
          sort_order: order,
        })));
    } catch { toast.error("Não foi possível reordenar"); }
    load();
  };

  const patchCategory = async (id, changes) => {
    try {
      await api.put(`/categories/${id}`, changes);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao guardar"); }
  };

  /**
   * Move a featured category up or down. Categories all start with sort_order
   * 0, so a plain swap would change nothing - the whole featured group is
   * renumbered and only the rows that actually changed are sent.
   */
  const moveCategory = async (id, direction) => {
    const featured = categories.filter((c) => c.is_featured);
    const index = featured.findIndex((c) => c.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= featured.length) return;

    const reordered = [...featured];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    const changed = reordered
      .map((c, i) => ({ c, order: i + 1 }))
      .filter(({ c, order }) => c.sort_order !== order);

    setCategories((prev) => prev.map((c) => {
      const hit = reordered.findIndex((r) => r.id === c.id);
      return hit === -1 ? c : { ...c, sort_order: hit + 1 };
    }).sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name)));

    try {
      await Promise.all(changed.map(({ c, order }) =>
        api.put(`/categories/${c.id}`, { sort_order: order })));
    } catch {
      toast.error("Não foi possível reordenar");
    }
    load();
  };

  if (!user || user === false) return <div className="min-h-screen grid place-items-center text-muted-foreground">A carregar...</div>;

  const featuredCount = categories.filter((c) => c.is_featured).length;

  return (
    <div className="min-h-screen bg-muted flex">
      <aside className="w-64 shrink-0 bg-foreground text-white p-6 hidden md:flex flex-col fixed h-screen">
        <div className="flex items-center gap-2 mb-10">
          <img src="/logo3duo.png" alt="3Duo" className="h-8 w-auto" />
          <span className="font-display font-extrabold text-lg">Admin</span>
        </div>
        <nav className="space-y-2 flex-1">
          <button data-testid="tab-produtos" onClick={() => setTab("produtos")} className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 font-medium transition-colors ${tab === "produtos" ? "brand-gradient" : "hover:bg-white/10"}`}><Package size={18} /> Produtos</button>
          <button data-testid="tab-categorias" onClick={() => setTab("categorias")} className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 font-medium transition-colors ${tab === "categorias" ? "brand-gradient" : "hover:bg-white/10"}`}><Tags size={18} /> Categorias</button>
          <button data-testid="tab-pedidos" onClick={() => setTab("pedidos")} className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 font-medium transition-colors ${tab === "pedidos" ? "brand-gradient" : "hover:bg-white/10"}`}><ShoppingBag size={18} /> Pedidos</button>
          <button data-testid="tab-definicoes" onClick={() => setTab("definicoes")} className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 font-medium transition-colors ${tab === "definicoes" ? "brand-gradient" : "hover:bg-white/10"}`}><Settings size={18} /> Definições</button>
          <a href="/" target="_blank" rel="noreferrer" className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 font-medium hover:bg-white/10 transition-colors"><ExternalLink size={18} /> Ver site</a>
        </nav>
        <button data-testid="logout-btn" onClick={() => { logout(); nav("/admin/login"); }} className="flex items-center gap-3 rounded-2xl px-4 py-3 font-medium hover:bg-white/10 transition-colors"><LogOut size={18} /> Sair</button>
      </aside>

      <main className="flex-1 md:ml-64 p-6 md:p-10">
        <div className="flex md:hidden gap-2 mb-6 overflow-x-auto pb-1">
          {[["produtos","Produtos"],["categorias","Categorias"],["pedidos","Pedidos"],["definicoes","Definições"]].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)} className={`whitespace-nowrap rounded-full px-4 py-2 font-semibold text-sm ${tab === k ? "brand-gradient text-white" : "bg-card border border-border"}`}>{l}</button>
          ))}
        </div>

        {tab === "produtos" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display font-black text-3xl tracking-tight">Produtos</h1>
                <p className="text-muted-foreground">{products.length} produtos no catálogo</p>
              </div>
              <button data-testid="new-product-btn" onClick={() => setEditing(EMPTY)} className="inline-flex items-center gap-2 rounded-full brand-gradient text-white font-semibold px-5 py-3 transition-transform hover:scale-105 active:scale-95"><Plus size={18} /> Novo Produto</button>
            </div>

            <div className="bg-card rounded-3xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/60 text-left">
                  <tr>
                    <th className="p-4 font-semibold">Produto</th>
                    <th className="p-4 font-semibold hidden md:table-cell">Categoria</th>
                    <th className="p-4 font-semibold hidden md:table-cell">Etiquetas</th>
                    <th className="p-4 font-semibold text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, i) => (
                    <tr key={p.id} data-testid={`admin-row-${p.id}`} className="border-t border-border">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-muted overflow-hidden shrink-0">
                            {p.images?.[0] && <img src={resolveImg(p.images[0])} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <span className="font-medium">{p.name}</span>
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell text-muted-foreground">{p.category}</td>
                      <td className="p-4 hidden md:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {p.is_new && <span className="rounded-full bg-secondary/30 text-xs px-2 py-0.5">Novo</span>}
                          {p.is_featured && <span className="rounded-full bg-primary/15 text-primary text-xs px-2 py-0.5">Destaque</span>}
                          {p.is_customizable && <span className="rounded-full bg-foreground/10 text-xs px-2 py-0.5">Personalizável</span>}
                          {p.videos?.length > 0 && <span className="rounded-full bg-foreground/10 text-xs px-2 py-0.5">{p.videos.length} vídeo(s)</span>}
                        </div>
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <button data-testid={`up-${p.id}`} onClick={() => moveProduct(p.id, -1)} disabled={i === 0}
                          aria-label="Subir" title="Mostrar mais acima"
                          className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-25"><ChevronUp size={16} /></button>
                        <button data-testid={`down-${p.id}`} onClick={() => moveProduct(p.id, 1)} disabled={i === products.length - 1}
                          aria-label="Descer" title="Mostrar mais abaixo"
                          className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-25"><ChevronDown size={16} /></button>
                        <button data-testid={`edit-${p.id}`} onClick={() => setEditing(p)} className="p-2 rounded-full hover:bg-muted transition-colors"><Pencil size={16} /></button>
                        <button data-testid={`delete-${p.id}`} onClick={() => removeProduct(p.id)} className="p-2 rounded-full hover:bg-destructive/10 text-destructive transition-colors"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "categorias" && (
          <>
            <h1 className="font-display font-black text-3xl tracking-tight mb-2">Categorias</h1>
            <p className="text-muted-foreground mb-6">
              As categorias marcadas como destaque aparecem na homepage
              {featuredCount > 0 ? ` — ${featuredCount} de momento.` : ". Nenhuma está em destaque, por isso a secção não aparece."}
            </p>

            <div className="flex gap-2 mb-6 max-w-md">
              <input data-testid="new-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCategory()}
                placeholder="Nova categoria..." className="flex-1 rounded-full border border-border bg-card px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" />
              <button data-testid="add-category-btn" onClick={addCategory} className="rounded-full brand-gradient text-white font-semibold px-5"><Plus size={18} /></button>
            </div>

            <div className="space-y-3">
              {categories.map((c, i) => (
                <CategoryRow
                  key={c.id}
                  category={c}
                  featuredIndex={c.is_featured ? categories.filter((x) => x.is_featured).findIndex((x) => x.id === c.id) : -1}
                  featuredTotal={featuredCount}
                  onPatch={(changes) => patchCategory(c.id, changes)}
                  onMove={(direction) => moveCategory(c.id, direction)}
                  onDelete={() => removeCategory(c)}
                />
              ))}
              {categories.length === 0 && (
                <p className="text-muted-foreground">Ainda não há categorias.</p>
              )}
            </div>
          </>
        )}

        {tab === "pedidos" && <OrdersPanel />}
        {tab === "definicoes" && <SettingsPanel />}
      </main>

      <AnimatePresence>
        {editing && (
          <ProductForm
            initial={editing}
            categories={categories}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
        {deleting && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="category-delete-dialog"
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4">
            <div className="bg-card rounded-[2rem] border border-border w-full max-w-md p-6">
              <h2 className="font-display font-bold text-xl">Remover "{deleting.category.name}"</h2>
              <p className="text-sm text-muted-foreground mt-2">{deleting.detail}</p>
              <p className="text-sm font-semibold mt-5 mb-2">Mover os produtos para:</p>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {categories.filter((c) => c.id !== deleting.category.id).map((c) => (
                  <button key={c.id} data-testid={`reassign-${c.id}`}
                    onClick={() => confirmRemoveCategory(c.name)}
                    className="w-full text-left rounded-2xl border border-border px-4 py-3 font-medium hover:bg-muted transition-colors">
                    {c.name}
                  </button>
                ))}
                <button data-testid="reassign-none" onClick={() => confirmRemoveCategory(null)}
                  className="w-full text-left rounded-2xl border border-dashed border-border px-4 py-3 text-muted-foreground hover:bg-muted transition-colors">
                  Deixar sem categoria
                </button>
              </div>
              <button onClick={() => setDeleting(null)}
                className="mt-5 w-full rounded-full border border-border py-3 font-semibold hover:bg-muted transition-colors">
                Cancelar
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CategoryRow({ category, featuredIndex, featuredTotal, onPatch, onMove, onDelete }) {
  const [uploading, setUploading] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(category.name);
  const Icon = iconFor(category.icon);

  const commitRename = () => {
    const name = draft.trim();
    setRenaming(false);
    if (!name || name === category.name) { setDraft(category.name); return; }
    onPatch({ name });
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.kind !== "image") { toast.error("Escolhe uma imagem."); return; }
      onPatch({ image: data.url });
    } catch {
      toast.error("Erro no upload");
    } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div data-testid={`cat-${category.id}`} className="bg-card rounded-2xl border border-border p-4 flex flex-wrap items-center gap-4">
      <div className="w-14 h-14 rounded-xl bg-muted overflow-hidden shrink-0 grid place-items-center">
        {category.image
          ? <img src={resolveImg(category.image)} alt="" className="w-full h-full object-cover" />
          : <Icon size={22} className="text-muted-foreground" />}
      </div>

      <div className="flex-1 min-w-[9rem]">
        {renaming ? (
          <input
            data-testid={`cat-name-input-${category.id}`}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") { setDraft(category.name); setRenaming(false); }
            }}
            className="w-full rounded-xl border border-border bg-background px-3 py-1.5 font-semibold outline-none focus:ring-2 focus:ring-primary/40"
          />
        ) : (
          <button
            data-testid={`cat-rename-${category.id}`}
            onClick={() => { setDraft(category.name); setRenaming(true); }}
            title="Clica para mudar o nome"
            className="group/name flex items-center gap-2 font-semibold text-left hover:text-primary transition-colors"
          >
            {category.name}
            <Pencil size={13} className="opacity-0 group-hover/name:opacity-60 transition-opacity" />
          </button>
        )}
        <p className="text-xs text-muted-foreground">{category.slug}</p>
      </div>

      <button
        data-testid={`cat-featured-${category.id}`}
        onClick={() => onPatch({ is_featured: !category.is_featured })}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
          category.is_featured ? "brand-gradient text-white border-transparent" : "border-border text-muted-foreground"
        }`}
      >
        <Star size={15} /> Destaque
      </button>

      <label className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-muted transition-colors">
        {uploading ? "..." : <><ImageIcon size={15} /> Imagem</>}
        <input data-testid={`cat-image-${category.id}`} type="file" accept="image/*" className="hidden" onChange={uploadImage} />
      </label>

      {category.image && (
        <button onClick={() => onPatch({ image: "" })} className="text-xs text-muted-foreground underline hover:text-foreground">
          remover imagem
        </button>
      )}

      <select
        data-testid={`cat-icon-${category.id}`}
        value={category.icon || ""}
        onChange={(e) => onPatch({ icon: e.target.value })}
        title="Ícone usado quando não há imagem"
        className="rounded-full border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
      >
        <option value="">Ícone…</option>
        {CATEGORY_ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>

      {category.is_featured && (
        <div className="flex items-center gap-1">
          <button data-testid={`cat-up-${category.id}`} onClick={() => onMove(-1)} disabled={featuredIndex <= 0}
            aria-label="Subir" className="p-2 rounded-full hover:bg-muted disabled:opacity-30"><ChevronUp size={16} /></button>
          <button data-testid={`cat-down-${category.id}`} onClick={() => onMove(1)} disabled={featuredIndex === featuredTotal - 1}
            aria-label="Descer" className="p-2 rounded-full hover:bg-muted disabled:opacity-30"><ChevronDown size={16} /></button>
        </div>
      )}

      <button data-testid={`del-cat-${category.id}`} onClick={onDelete} className="p-2 rounded-full hover:bg-destructive/10 text-destructive"><Trash2 size={15} /></button>
    </div>
  );
}

function ProductForm({ initial, categories, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoLink, setVideoLink] = useState("");
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [aiReady, setAiReady] = useState(false);
  const [describing, setDescribing] = useState(false);
  const isEdit = !!initial.id;

  useEffect(() => {
    api.get("/admin/ai/status")
      .then((r) => setAiReady(!!r.data.enabled))
      .catch(() => setAiReady(false));
  }, []);

  // Writes a description from the photographs. Replaces what is there, so it
  // asks first when the field already has text the user may want to keep.
  const describeFrom = async (images) => {
    if (!images.length) { toast.error("Adiciona primeiro uma fotografia."); return; }
    if (form.description.trim() &&
        !window.confirm("Isto substitui a descrição atual. Continuar?")) return;
    setDescribing(true);
    try {
      const { data } = await api.post("/admin/ai/describe", {
        images, name: form.name, category: form.category,
      });
      set("description", data.description);
      toast.success(`Descrição gerada a partir de ${data.images_used} foto(s). Revê antes de guardar.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível gerar a descrição.");
    } finally { setDescribing(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => (data.kind === "video"
      ? { ...f, videos: [...f.videos, data.url] }
      : { ...f, images: [...f.images, data.url] }));
    return data;
  };

  // Images go through the editor one at a time before being uploaded; videos
  // are far too large to hold in memory for editing, so they upload directly.
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    const isVideo = (f) => /\.(mp4|webm|mov)$/i.test(f.name);
    const videos = files.filter(isVideo);
    const images = files.filter((f) => !isVideo(f));

    if (videos.length) {
      setUploading(true);
      try {
        for (const video of videos) await uploadOne(video);
        toast.success(`${videos.length} vídeo(s) carregado(s)`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erro no upload do vídeo");
      } finally { setUploading(false); }
    }

    if (images.length) { setQueue(images); setQueueIndex(0); }
  };

  const advanceQueue = () => {
    setQueueIndex((i) => {
      if (i + 1 < queue.length) return i + 1;
      setQueue([]);
      return 0;
    });
  };

  const onEdited = async (processed, error) => {
    if (error) {
      toast.error("Não foi possível processar a imagem");
    } else if (processed) {
      setUploading(true);
      try {
        await uploadOne(processed);
        toast.success("Imagem carregada");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erro no upload");
      } finally { setUploading(false); }
    }
    advanceQueue();
  };

  // The first image is the cover used on cards and listings, so reordering is
  // how the cover gets chosen.
  const moveImage = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= form.images.length) return;
    const images = [...form.images];
    [images[index], images[target]] = [images[target], images[index]];
    setForm((f) => ({ ...f, images, variants: orderVariants(f.variants, images) }));
  };

  // Variants are kept in the same order as the photographs they belong to.
  const orderVariants = (variants, images) =>
    images.map((im) => (variants || []).find((v) => v.image === im)).filter(Boolean);

  const variantFor = (image) => (form.variants || []).find((v) => v.image === image);
  const labelFor = (image) => variantFor(image)?.label || "";

  // Naming a photograph turns it into a selectable model; clearing the name
  // turns it back into an ordinary gallery image.
  const setVariantLabel = (image, label) => {
    setForm((f) => {
      const existing = (f.variants || []).find((v) => v.image === image);
      const others = (f.variants || []).filter((v) => v.image !== image);
      // Renaming must not lose the size and weight already typed in; clearing
      // the name discards them, because they describe a model, not a photo.
      const next = label.trim()
        ? [...others, { dimensions: "", weight: "", ...existing, image, label }]
        : others;
      return { ...f, variants: orderVariants(next, f.images) };
    });
  };

  const setVariantField = (image, field, value) => {
    setForm((f) => ({
      ...f,
      variants: (f.variants || []).map((v) => (v.image === image ? { ...v, [field]: value } : v)),
    }));
  };

  const removeImage = (index) => {
    setForm((f) => {
      const image = f.images[index];
      const images = f.images.filter((_, i) => i !== index);
      return {
        ...f,
        images,
        // A model whose photograph is gone cannot be chosen any more.
        variants: (f.variants || []).filter((v) => v.image !== image),
      };
    });
  };

  const addVideoLink = () => {
    const url = videoLink.trim();
    if (!url) return;
    if (!isSupportedVideoLink(url)) {
      toast.error("Só são suportados links do YouTube ou Vimeo.");
      return;
    }
    if (form.videos.includes(url)) { toast.error("Esse vídeo já está na lista."); return; }
    set("videos", [...form.videos, url]);
    setVideoLink("");
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Indica o nome do produto"); return; }
    if (form.is_customizable && !form.customization_label.trim()) {
      toast.error("Indica o que deve ser pedido ao cliente na personalização.");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name, description: form.description, category: form.category,
      price: form.price || null, images: form.images, videos: form.videos,
      dimensions: form.dimensions || "", weight: form.weight || "",
      variants: form.variants || [],
      is_new: form.is_new, is_featured: form.is_featured,
      is_customizable: form.is_customizable,
      customization_label: form.is_customizable ? form.customization_label.trim() : "",
      sort_order: form.sort_order ?? 0,
    };
    try {
      if (isEdit) await api.put(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success(isEdit ? "Produto atualizado" : "Produto criado");
      onSaved();
    } catch (e) { toast.error("Erro ao guardar"); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="product-form"
        className="bg-card rounded-[2rem] border border-border w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card rounded-t-[2rem] z-10">
          <h2 className="font-display font-bold text-xl">{isEdit ? "Editar Produto" : "Novo Produto"}</h2>
          <button onClick={onClose} data-testid="close-form" className="p-2 rounded-full hover:bg-muted"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-semibold">Nome</label>
            <input data-testid="form-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm font-semibold">Descrição</label>
              {aiReady && (
                <button type="button" data-testid="ai-describe-all"
                  onClick={() => describeFrom(form.images)}
                  disabled={describing || !form.images.length}
                  title="Analisa todas as fotografias e escreve a descrição"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-40">
                  <Sparkles size={13} /> {describing ? "A escrever..." : "Gerar com IA"}
                </button>
              )}
            </div>
            <textarea data-testid="form-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold">Categoria</label>
              <select data-testid="form-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40">
                <option value="">— Selecionar —</option>
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold">Preço (opcional)</label>
              <input data-testid="form-price" value={form.price || ""} onChange={(e) => set("price", e.target.value)} placeholder="14,90" className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" />
              <p className="text-xs text-muted-foreground mt-1">O símbolo € é acrescentado automaticamente no fim.</p>
            </div>
            <div>
              <label className="text-sm font-semibold">Tamanho (opcional)</label>
              <input data-testid="form-dimensions" value={form.dimensions || ""} onChange={(e) => set("dimensions", e.target.value)} placeholder="12 × 8 × 5 cm" className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" />
            </div>
            <div>
              <label className="text-sm font-semibold">Peso (opcional)</label>
              <input data-testid="form-weight" value={form.weight || ""} onChange={(e) => set("weight", e.target.value)} placeholder="85 g" className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            Escreve a unidade tu (cm, mm, g, kg) — não é acrescentada automaticamente, para não
            adivinhar errado. Se um modelo tiver medidas próprias, preenche-as na foto desse modelo.
          </p>

          <div>
            <label className="text-sm font-semibold">Fotografias e vídeos</label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Imagens: JPG, PNG, GIF, WEBP. Vídeos: MP4, WEBM, MOV.
              A primeira imagem é a capa. <span className="font-medium">Dá um nome a uma foto
              para a tornar um modelo que o cliente escolhe</span> (ex.: "Dragão azul").
            </p>
            <div className="flex flex-wrap gap-3 mt-2">
              {form.images.map((im, i) => (
                <div key={im} className="flex flex-col gap-1 w-36">
                  <div className="relative w-36 h-36 rounded-xl overflow-hidden border border-border">
                    <img src={resolveImg(im)} alt="" className="w-full h-full object-cover" />
                    {i === 0 && (
                      <span className="absolute bottom-0 inset-x-0 bg-foreground/80 text-white text-[10px] font-bold text-center py-0.5">
                        CAPA
                      </span>
                    )}
                    <button onClick={() => removeImage(i)}
                      aria-label="Remover imagem" className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X size={12} /></button>
                    {aiReady && (
                      <button type="button" data-testid={`ai-describe-${i}`}
                        onClick={() => describeFrom([im])} disabled={describing}
                        aria-label="Descrever só esta foto" title="Escrever a descrição só a partir desta foto"
                        className="absolute top-1 left-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80 disabled:opacity-40">
                        <Sparkles size={11} />
                      </button>
                    )}
                  </div>
                  <input
                    data-testid={`variant-label-${i}`}
                    value={labelFor(im)}
                    onChange={(e) => setVariantLabel(im, e.target.value)}
                    placeholder="Nome do modelo"
                    title="Dá um nome para esta foto se tornar um modelo à escolha"
                    className="w-full rounded-lg border border-border bg-background px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  {/* Only a named photo is a model, and only a model can have
                      its own measurements — so these appear with the name. */}
                  {labelFor(im) && (
                    <>
                      <input
                        data-testid={`variant-dimensions-${i}`}
                        value={variantFor(im)?.dimensions || ""}
                        onChange={(e) => setVariantField(im, "dimensions", e.target.value)}
                        placeholder="Tamanho deste modelo"
                        title="Deixa vazio para usar o tamanho do produto"
                        className="w-full rounded-lg border border-border bg-background px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <input
                        data-testid={`variant-weight-${i}`}
                        value={variantFor(im)?.weight || ""}
                        onChange={(e) => setVariantField(im, "weight", e.target.value)}
                        placeholder="Peso deste modelo"
                        title="Deixa vazio para usar o peso do produto"
                        className="w-full rounded-lg border border-border bg-background px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </>
                  )}
                  <div className="flex justify-center gap-0.5">
                    <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0}
                      aria-label="Mover para trás" title="Mover para trás"
                      className="p-1 rounded hover:bg-muted disabled:opacity-25"><ChevronLeft size={13} /></button>
                    <button type="button" onClick={() => moveImage(i, 1)} disabled={i === form.images.length - 1}
                      aria-label="Mover para a frente" title="Mover para a frente"
                      className="p-1 rounded hover:bg-muted disabled:opacity-25"><ChevronRight size={13} /></button>
                  </div>
                </div>
              ))}
              <label data-testid="upload-label" className="w-20 h-20 rounded-xl border-2 border-dashed border-border grid place-items-center cursor-pointer hover:border-primary transition-colors text-muted-foreground">
                {uploading ? <span className="text-xs">...</span> : <Upload size={20} />}
                <input data-testid="upload-input" type="file" accept="image/*,video/*" multiple className="hidden" onChange={onUpload} />
              </label>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold flex items-center gap-2"><Film size={15} /> Vídeos</label>
            <div className="flex gap-2 mt-2">
              <input
                data-testid="video-link-input"
                value={videoLink}
                onChange={(e) => setVideoLink(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVideoLink(); } }}
                placeholder="Colar link do YouTube ou Vimeo..."
                className="flex-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button type="button" data-testid="add-video-link" onClick={addVideoLink}
                className="rounded-2xl border border-border px-4 font-semibold hover:bg-muted transition-colors inline-flex items-center gap-2">
                <Link2 size={16} /> Adicionar
              </button>
            </div>
            {form.videos.length > 0 && (
              <ul className="mt-3 space-y-2">
                {form.videos.map((v, i) => (
                  <li key={v} data-testid={`video-${i}`} className="flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-2 text-sm">
                    {embedUrl(v) ? <Link2 size={15} className="shrink-0 text-muted-foreground" /> : <Film size={15} className="shrink-0 text-muted-foreground" />}
                    <span className="truncate flex-1">{embedUrl(v) ? v : v.split("/").pop()}</span>
                    <button onClick={() => set("videos", form.videos.filter((_, idx) => idx !== i))}
                      aria-label="Remover vídeo" className="p-1 rounded-full hover:bg-destructive/10 text-destructive"><X size={14} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-2xl border border-border p-4">
            <button
              type="button"
              data-testid="toggle-customizable"
              onClick={() => set("is_customizable", !form.is_customizable)}
              className={`flex items-center gap-2 rounded-full px-4 py-2 font-semibold text-sm border transition-colors ${
                form.is_customizable ? "bg-foreground text-white border-transparent" : "border-border text-muted-foreground"
              }`}
            >
              <Pencil size={15} /> Produto personalizável
            </button>
            {form.is_customizable && (
              <div className="mt-4">
                <label className="text-sm font-semibold">O que pedir ao cliente</label>
                <input
                  data-testid="form-customization-label"
                  value={form.customization_label}
                  onChange={(e) => set("customization_label", e.target.value)}
                  placeholder="Ex.: Nome a gravar no porta-chaves"
                  className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Aparece na caixa que o cliente preenche antes de adicionar ao orçamento. É obrigatório preencher.
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button data-testid="toggle-new" onClick={() => set("is_new", !form.is_new)} className={`flex items-center gap-2 rounded-full px-4 py-2 font-semibold text-sm border transition-colors ${form.is_new ? "bg-secondary text-foreground border-secondary" : "border-border text-muted-foreground"}`}><Sparkles size={15} /> Novo</button>
            <button data-testid="toggle-featured" onClick={() => set("is_featured", !form.is_featured)} className={`flex items-center gap-2 rounded-full px-4 py-2 font-semibold text-sm border transition-colors ${form.is_featured ? "brand-gradient text-white border-transparent" : "border-border text-muted-foreground"}`}><Star size={15} /> Em Destaque</button>
          </div>
        </div>

        <div className="p-6 border-t border-border flex justify-end gap-3 sticky bottom-0 bg-card rounded-b-[2rem]">
          <button onClick={onClose} className="rounded-full border border-border px-6 py-3 font-semibold hover:bg-muted transition-colors">Cancelar</button>
          <button data-testid="save-product" onClick={save} disabled={saving} className="rounded-full brand-gradient text-white px-6 py-3 font-semibold transition-transform hover:scale-105 active:scale-95 disabled:opacity-60">{saving ? "A guardar..." : "Guardar"}</button>
        </div>
      </motion.div>

      <AnimatePresence>
        {queue.length > 0 && (
          <ImageEditor
            key={queueIndex}
            file={queue[queueIndex]}
            index={queueIndex}
            total={queue.length}
            onCancel={advanceQueue}
            onDone={onEdited}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
